/**
 * Service de Debug pour l'API Yiatou
 *
 * Documentation API: https://dev.api.chinexpress.com/docs/api#
 *
 * Ce service permet de:
 * - Logger les requêtes/réponses API
 * - Mesurer les temps de réponse
 * - Afficher les erreurs de manière détaillée
 * - Stocker l'historique des appels pour analyse
 */

const API_DOCS_URL = 'https://dev.api.chinexpress.com/docs/api#';
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

class DebugService {
  constructor() {
    this.isEnabled = process.env.NODE_ENV === 'development';
    this.logHistory = [];
    this.maxHistorySize = 100;
  }

  /**
   * URLs de référence
   */
  static get API_DOCS_URL() {
    return API_DOCS_URL;
  }

  static get API_BASE_URL() {
    return API_BASE_URL;
  }

  /**
   * Active/désactive le mode debug
   */
  setEnabled(enabled) {
    this.isEnabled = enabled;
    this.log('info', `Debug mode ${enabled ? 'activé' : 'désactivé'}`);
  }

  /**
   * Ouvre la documentation API dans un nouvel onglet
   */
  openApiDocs() {
    window.open(API_DOCS_URL, '_blank');
  }

  /**
   * Log générique avec timestamp
   */
  log(level, message, data = null) {
    if (!this.isEnabled) return;

    const timestamp = new Date().toISOString();
    const logEntry = { timestamp, level, message, data };

    // Ajouter à l'historique
    this.logHistory.push(logEntry);
    if (this.logHistory.length > this.maxHistorySize) {
      this.logHistory.shift();
    }

    // Afficher dans la console avec style
    const styles = {
      info: 'color: #0066cc; font-weight: bold;',
      success: 'color: #00a650; font-weight: bold;',
      warning: 'color: #ff9800; font-weight: bold;',
      error: 'color: #dc2626; font-weight: bold;',
      request: 'color: #9c27b0; font-weight: bold;',
      response: 'color: #2196f3; font-weight: bold;',
    };

    console.log(`%c[${level.toUpperCase()}] ${timestamp}`, styles[level] || styles.info, message);
    if (data) {
      console.log(data);
    }
  }

  /**
   * Log une requête API sortante
   */
  logRequest(method, url, data = null, headers = {}) {
    if (!this.isEnabled) return;

    const requestInfo = {
      method: method.toUpperCase(),
      url,
      data,
      headers: this.sanitizeHeaders(headers),
      timestamp: new Date().toISOString(),
    };

    this.log('request', `➡️ ${method.toUpperCase()} ${url}`, requestInfo);

    // Retourner un ID pour matcher avec la réponse
    return Date.now();
  }

  /**
   * Log une réponse API
   */
  logResponse(requestId, response, duration = null) {
    if (!this.isEnabled) return;

    const status = response?.status || 'unknown';
    const statusText = response?.statusText || '';
    const data = response?.data;

    const responseInfo = {
      status,
      statusText,
      data,
      duration: duration ? `${duration}ms` : null,
      headers: response?.headers,
    };

    const emoji = status >= 200 && status < 300 ? '✅' : '❌';
    this.log('response', `${emoji} Response ${status} ${statusText}`, responseInfo);
  }

  /**
   * Log une erreur API avec détails
   */
  logError(error, context = '') {
    if (!this.isEnabled) return;

    const errorInfo = {
      context,
      message: error?.message,
      status: error?.response?.status,
      statusText: error?.response?.statusText,
      data: error?.response?.data,
      url: error?.config?.url,
      method: error?.config?.method,
      validationErrors: error?.response?.data?.errors,
      stack: error?.stack,
      apiDocsUrl: API_DOCS_URL,
    };

    this.log('error', `🚨 Erreur API: ${context}`, errorInfo);

    // Suggestions de debug
    if (error?.response?.status === 401) {
      this.log('warning', '💡 Suggestion: Token expiré ou invalide. Vérifier authService.getToken()');
    } else if (error?.response?.status === 422) {
      this.log('warning', '💡 Suggestion: Erreur de validation. Vérifier le format des données envoyées.');
      this.log('info', `📖 Documentation API: ${API_DOCS_URL}`);
    } else if (error?.response?.status === 404) {
      this.log('warning', '💡 Suggestion: Endpoint non trouvé. Vérifier l\'URL et la documentation API.');
    } else if (error?.response?.status >= 500) {
      this.log('warning', '💡 Suggestion: Erreur serveur. Vérifier les logs backend.');
    }
  }

  /**
   * Créer un wrapper pour mesurer le temps d'exécution d'une fonction async
   */
  async measure(name, asyncFn) {
    if (!this.isEnabled) {
      return asyncFn();
    }

    const start = performance.now();
    this.log('info', `⏱️ Début: ${name}`);

    try {
      const result = await asyncFn();
      const duration = Math.round(performance.now() - start);
      this.log('success', `⏱️ Fin: ${name} (${duration}ms)`);
      return result;
    } catch (error) {
      const duration = Math.round(performance.now() - start);
      this.log('error', `⏱️ Échec: ${name} (${duration}ms)`, error);
      throw error;
    }
  }

  /**
   * Affiche un résumé de l'état actuel
   */
  printStatus() {
    console.log('%c📊 Debug Status - Yiatou API', 'font-size: 16px; font-weight: bold; color: #FF5722;');
    console.log('━'.repeat(50));
    console.log(`🔗 API Base URL: ${API_BASE_URL}`);
    console.log(`📖 Documentation: ${API_DOCS_URL}`);
    console.log(`🐛 Debug enabled: ${this.isEnabled}`);
    console.log(`📝 Log history: ${this.logHistory.length} entries`);
    console.log(`🔑 Auth token: ${localStorage.getItem('authToken') ? 'Present' : 'Missing'}`);
    console.log('━'.repeat(50));
  }

  /**
   * Affiche l'historique des logs
   */
  printHistory(filter = null) {
    let logs = this.logHistory;

    if (filter) {
      logs = logs.filter(log => log.level === filter);
    }

    console.log(`%c📜 Log History (${logs.length} entries)`, 'font-size: 14px; font-weight: bold;');
    console.table(logs.map(log => ({
      time: log.timestamp.split('T')[1].split('.')[0],
      level: log.level,
      message: log.message.substring(0, 50),
    })));
  }

  /**
   * Efface l'historique des logs
   */
  clearHistory() {
    this.logHistory = [];
    this.log('info', '🗑️ Historique effacé');
  }

  /**
   * Exporte l'historique en JSON
   */
  exportHistory() {
    const data = JSON.stringify(this.logHistory, null, 2);
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `yiatou-debug-${Date.now()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    this.log('info', '📤 Historique exporté');
  }

  /**
   * Teste la connexion à l'API
   */
  async testConnection() {
    this.log('info', '🔌 Test de connexion à l\'API...');

    try {
      const response = await fetch(`${API_BASE_URL}/health`, {
        method: 'GET',
        headers: { 'Accept': 'application/json' },
      });

      if (response.ok) {
        this.log('success', '✅ Connexion API OK');
        return true;
      } else {
        this.log('warning', `⚠️ API répond avec status ${response.status}`);
        return false;
      }
    } catch (error) {
      this.log('error', '❌ Impossible de contacter l\'API', error.message);
      return false;
    }
  }

  /**
   * Supprime les infos sensibles des headers pour le log
   */
  sanitizeHeaders(headers) {
    const sanitized = { ...headers };
    if (sanitized.Authorization) {
      sanitized.Authorization = 'Bearer [HIDDEN]';
    }
    return sanitized;
  }

  /**
   * Intercepteur Axios pour le debug automatique
   * Usage: axios.interceptors.request.use(debugService.createRequestInterceptor())
   */
  createRequestInterceptor() {
    return (config) => {
      if (this.isEnabled) {
        config._debugRequestId = this.logRequest(
          config.method,
          config.url,
          config.data,
          config.headers
        );
        config._debugStartTime = performance.now();
      }
      return config;
    };
  }

  /**
   * Intercepteur Axios pour les réponses
   * Usage: axios.interceptors.response.use(debugService.createResponseInterceptor())
   */
  createResponseInterceptor() {
    return (response) => {
      if (this.isEnabled && response.config._debugRequestId) {
        const duration = Math.round(performance.now() - response.config._debugStartTime);
        this.logResponse(response.config._debugRequestId, response, duration);
      }
      return response;
    };
  }

  /**
   * Intercepteur Axios pour les erreurs
   * Usage: axios.interceptors.response.use(null, debugService.createErrorInterceptor())
   */
  createErrorInterceptor() {
    return (error) => {
      if (this.isEnabled) {
        const context = `${error.config?.method?.toUpperCase()} ${error.config?.url}`;
        this.logError(error, context);
      }
      return Promise.reject(error);
    };
  }
}

// Instance singleton
const debugService = new DebugService();

// Exposer globalement en dev pour accès console
if (process.env.NODE_ENV === 'development') {
  window.debugService = debugService;
  window.apiDocs = () => debugService.openApiDocs();

  // Message d'accueil
  console.log('%c🐛 Yiatou Debug Service Ready', 'font-size: 14px; font-weight: bold; color: #FF5722;');
  console.log('Commandes disponibles dans la console:');
  console.log('  debugService.printStatus()  - Afficher le status');
  console.log('  debugService.printHistory() - Afficher l\'historique');
  console.log('  debugService.testConnection() - Tester l\'API');
  console.log('  apiDocs() - Ouvrir la documentation API');
}

export default debugService;
